// Elenco dei coupon di un cod_bolla, con emissione del DDT e cancellazione.
// Vedi SPECCOUPON2026.md, sezione "Elenco coupon".
import React, { useState, useRef, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import { usePesiPonte } from './queries';
import { TARA_DA_VEICOLO } from './couponMode';

// Elenco dei cod_bolla per il select, con i metadati (da cui la pesa)
const COD_BOLLA_LIST = gql`
  query CouponCodBollaList {
    codBollaList: ghiaia3_cod_bolla_metadati {
      cod_bolla
      metadati: ddt
    }
  }
`;

const COUPON_LIST = gql`
  query CouponList($cod_bolla: bpchar!) {
    ghiaia3_ddtcoupon(
      where: { cod_bolla: { _eq: $cod_bolla } }
      order_by: { targa: asc }
    ) {
      cod_bolla
      targa
      rag_soc
      cod_commessa
      articoli {
        descrizion
      }
      destinazione
      tara
      dtara
      dlordo
      cellulare
      stampante
      treg
      esito_sms
    }
  }
`;

const COUPON_DELETE = gql`
  mutation CouponDelete($cod_bolla: bpchar!, $targa: String!) {
    delete_ghiaia3_ddtcoupon_by_pk(cod_bolla: $cod_bolla, targa: $targa) {
      cod_bolla
      targa
    }
  }
`;

// Il lordo non si passa: lo preleva la funzione dalla pesa (SPECS_COUPON.md)
const EMETTI_DDT = gql`
  mutation EmettiDdtDaCoupon($cod_bolla: bpchar!, $targa: String!, $forza: Boolean) {
    ghiaia3_emetti_ddt_da_coupon(
      args: { p_cod_bolla: $cod_bolla, p_targa: $targa, p_forza: $forza }
    ) {
      result
    }
  }
`;

const fmtOra = (t) => t
  ? new Date(t).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
  : '';

// La pesa da interrogare e' quella configurata nei metadati del cod_bolla,
// altrimenti il cod_bolla stesso: stessa regola della funzione SQL.
const pesaDiCodBolla = (metadati, cod_bolla) => metadati?.pesa || cod_bolla || null;

// Riquadro del peso attualmente sulla pesa. pesiponte e' in quintali.
function PesoPesa({ codBolla, pesa }) {
  const { pesi, pollingInterval } = usePesiPonte(pesa);
  const ultimo = pesi?.[0];
  const vuoto = !ultimo || ultimo.peso <= 0;

  return (
    <div className="p-3 mt-3 border rounded bg-light"
         style={{ display: 'flex', alignItems: 'baseline', gap: '1.5rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 'small' }}>
        Pesa <b>{pesa || '—'}</b>{pesa !== codBolla && codBolla ? ` (cod. bolla ${codBolla})` : ''}
      </span>
      <span style={{ fontSize: '2rem', fontWeight: 600 }}>
        {vuoto ? '—' : (ultimo.peso / 10).toFixed(2)}
        {!vuoto && <span style={{ fontSize: '1rem', fontWeight: 400 }}> t</span>}
      </span>
      <span style={{ fontSize: 'small' }}>
        {!pesa ? 'nessuna pesa'
          : vuoto ? 'ponte vuoto'
          : `lettura delle ${fmtOra(ultimo.data)}`}
      </span>
      <span style={{ fontSize: 'x-small', opacity: 0.6 }}>
        aggiornamento ogni {pollingInterval / 1000}s
      </span>
    </div>
  );
}

export function ElencoCoupon() {
  const [codBolla, setCodBolla] = useState(null);
  const [filtroTarga, setFiltroTarga] = useState('');
  const [daEliminare, setDaEliminare] = useState(null);   // riga in attesa di conferma
  const [daForzare, setDaForzare] = useState(null);       // {row, esito} dopo un rifiuto
  const toast = useRef(null);

  const { data: cbData } = useQuery(COD_BOLLA_LIST);
  // Stessa forma di opzioni e template dello step cod_bolla del wizard.
  // Si elencano solo i cod_bolla che hanno una pesa configurata e la stampa
  // attiva: senza pesa non c'e' da dove prendere il lordo, e un coupon che non
  // stampa viene comunque rifiutato dalla funzione di emissione.
  const opzioni = (cbData?.codBollaList || [])
    .filter(c => Boolean(c.metadati?.pesa) && c.metadati?.stampare === true)
    // list_order crescente; a parita' il cod_bolla, perche' la query non ha un
    // ORDER BY e diversi codici condividono lo stesso list_order
    .sort((a, b) => (a.metadati?.list_order ?? 9999) - (b.metadati?.list_order ?? 9999)
                    || a.cod_bolla.localeCompare(b.cod_bolla))
    .map(c => ({
      cod_bolla: c.cod_bolla,
      descrizione: c.metadati?.descrizione || '',
      ddt: c.metadati,
    }));

  // Il primo codice bolla dell'elenco e' la scelta di partenza
  const primoCodBolla = opzioni[0]?.cod_bolla;
  useEffect(() => {
    if (!codBolla && primoCodBolla) setCodBolla(primoCodBolla);
  }, [codBolla, primoCodBolla]);

  const selectedOption = opzioni.find(o => o.cod_bolla === codBolla) || null;
  const pesa = pesaDiCodBolla(selectedOption?.ddt, codBolla);

  const optionTemplate = (option) => <span>{option.cod_bolla} - {option.descrizione}</span>;
  const valueTemplate = (option) => option
    ? <span>{option.cod_bolla} - {option.descrizione}</span>
    : <span>Seleziona codice bolla</span>;

  // Niente cache: i coupon li creano molti utenti da altre sessioni, quindi una
  // risposta memorizzata e' quasi sempre gia' vecchia. La creazione avviene nel
  // wizard, che passa da verifica_valori_ddt_e_salva e restituisce un jsonb:
  // Apollo non ha modo di collegarlo a questo elenco e di aggiornarlo da se'.
  // Il polling serve a far comparire anche i coupon creati da altri.
  const { data, loading, refetch } = useQuery(COUPON_LIST, {
    variables: { cod_bolla: codBolla },
    skip: !codBolla,
    fetchPolicy: 'no-cache',
    pollInterval: 10000,
  });

  // La ricerca lavora sulle righe gia' caricate: e' per sottostringa, non per
  // iniziale come le ricerche del wizard, perche' qui si cerca dentro un elenco
  // gia' visibile e non si propongono valori da verificare.
  const cerca = filtroTarga.trim().toUpperCase();
  const righe = (data?.ghiaia3_ddtcoupon || [])
    .filter(r => !cerca || r.targa.toUpperCase().includes(cerca));

  const [eliminaCoupon, { loading: eliminando }] = useMutation(COUPON_DELETE);
  const [emettiDdt, { loading: emettendo }] = useMutation(EMETTI_DDT);

  const mostra = (severity, summary, detail, life = 6000) =>
    toast.current?.show({ severity, summary, detail, life });

  const onElimina = async (row) => {
    try {
      await eliminaCoupon({ variables: { cod_bolla: row.cod_bolla, targa: row.targa } });
      mostra('success', 'Coupon eliminato', `${row.cod_bolla} ${row.targa}`);
      refetch();
    } catch (e) {
      mostra('error', 'Eliminazione fallita', e.message, 10000);
    } finally {
      setDaEliminare(null);
    }
  };

  const onEmetti = async (row, forza = false) => {
    setDaForzare(null);
    try {
      const { data } = await emettiDdt({
        variables: { cod_bolla: row.cod_bolla, targa: row.targa, forza },
      });
      const esito = data?.ghiaia3_emetti_ddt_da_coupon?.[0]?.result;
      if (esito?.ddt_emesso) {
        mostra('success', `DDT ${esito.cod_bolla} n. ${esito.num_bolla}`,
          `lordo ${esito.lordo} − tara ${esito.tara} = netto ${esito.netto}`, 10000);
      } else if (esito?.num_bolla) {
        // rifiutato perche' un DDT per la stessa coppia e' recente: si puo' forzare
        setDaForzare({ row, esito });
      } else {
        mostra('warn', 'DDT non emesso', esito?.esito || 'esito non disponibile', 10000);
      }
    } catch (e) {
      mostra('error', 'Emissione fallita', e.message, 10000);
    }
  };

  const taraBody = (row) => row.tara === TARA_DA_VEICOLO
    ? <span title="tara del veicolo">da veicolo</span>
    : <span>{row.tara}</span>;

  const correzioneBody = (campo) => (row) => {
    const v = Number(row[campo] ?? 0);
    if (!v) return null;
    return <span>{v > 0 ? `+${v}` : v}</span>;
  };

  const azioniBody = (row) => (
    <div style={{ display: 'flex', gap: '.5rem' }}>
      <Button label="Emetti DDT" icon="pi pi-file" size="small"
              disabled={emettendo} onClick={() => onEmetti(row)} />
      <Button icon="pi pi-trash" severity="danger" outlined size="small"
              tooltip="Elimina coupon" disabled={eliminando}
              onClick={() => setDaEliminare(row)} />
    </div>
  );

  return (
    <div className="card shadow">
      <Toast ref={toast} />

      <div className="card-body">
        <h2 style={{ marginBottom: '1em' }}>
          Elenco Coupon{selectedOption ? ` ${selectedOption.cod_bolla}` : ''}
        </h2>

        <div className="card-title">
          <div className="row align-items-end">
            <div className="col-xxl-3 col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12">
              <label htmlFor="cod_bolla" className="form-label">Codice Bolla</label>
              <Dropdown
                id="cod_bolla"
                value={selectedOption}
                options={opzioni}
                onChange={(e) => setCodBolla(e.value?.cod_bolla || '')}
                optionLabel="cod_bolla"
                itemTemplate={optionTemplate}
                valueTemplate={valueTemplate}
                placeholder="Seleziona codice bolla"
                className="w-100"
              />
            </div>
            <div className="col-xxl-3 col-xl-4 col-lg-4 col-md-5 col-sm-6 col-12">
              <label htmlFor="cercaTarga" className="form-label">Targa</label>
              <span className="p-input-icon-left w-100">
                <i className="pi pi-search" />
                <InputText id="cercaTarga" value={filtroTarga} className="w-100"
                           onChange={(e) => setFiltroTarga(e.target.value)}
                           placeholder="cerca targa" />
              </span>
            </div>
            <div className="col">
              {cerca && <span style={{ fontSize: 'small' }}>
                {righe.length} di {data?.ghiaia3_ddtcoupon?.length ?? 0}
              </span>}
            </div>
          </div>
        </div>

        <PesoPesa codBolla={codBolla} pesa={pesa} />
      </div>

      <div className="card-body">
      <DataTable value={righe} loading={loading}
                 emptyMessage={!codBolla ? 'Scegliere un codice bolla'
                                : cerca ? 'Nessuna targa corrisponde alla ricerca'
                                : 'Nessun coupon per questo codice bolla'}
                 dataKey="targa" style={{ fontSize: 'small' }}>
        <Column field="targa" header="Targa" />
        <Column field="rag_soc" header="Cliente" />
        <Column field="cod_commessa" header="Commessa" />
        <Column field="articoli.descrizion" header="Articolo" />
        <Column field="destinazione" header="Destinazione" />
        <Column header="Tara" body={taraBody} />
        <Column header="Δtara" body={correzioneBody('dtara')} />
        <Column header="Δlordo" body={correzioneBody('dlordo')} />
        <Column field="cellulare" header="Cellulare" />
        <Column field="stampante" header="Stampante" />
        <Column header="Registrato" body={(row) => fmtOra(row.treg)} />
        <Column header="Azioni" body={azioniBody} />
      </DataTable>
      </div>

      <Dialog header="Eliminare il coupon?" visible={!!daEliminare} modal
              onHide={() => setDaEliminare(null)}
              footer={
                <>
                  <Button label="Annulla" text onClick={() => setDaEliminare(null)} />
                  <Button label="Elimina" severity="danger" disabled={eliminando}
                          onClick={() => onElimina(daEliminare)} />
                </>
              }>
        {daEliminare &&
          <span>Coupon <b>{daEliminare.cod_bolla} {daEliminare.targa}</b> di {daEliminare.rag_soc}.</span>}
      </Dialog>

      <Dialog header="DDT già emesso di recente" visible={!!daForzare} modal
              onHide={() => setDaForzare(null)}
              footer={
                <>
                  <Button label="Annulla" text onClick={() => setDaForzare(null)} />
                  <Button label="Emetti comunque" severity="warning" disabled={emettendo}
                          onClick={() => onEmetti(daForzare.row, true)} />
                </>
              }>
        {daForzare && <span>{daForzare.esito.esito}</span>}
      </Dialog>
    </div>
  );
}
